/**
 * Add one blackout range.
 *
 * The smaller half of the rate write path. `agreement_blackout` is 1:N because
 * the RFP takes a LIST of ranges, and ranges are INCLUSIVE — a single blacked
 * out night is the same date twice, which the form says rather than leaving
 * someone to guess whether the end date is included.
 *
 * NO EDIT, matching the API: a range is two dates and a note, and there is no
 * correction that remove-and-re-add does not express. Removal lives on the row
 * itself, where the reader can see which range they are removing.
 */
import { useState } from 'react';
import { Modal } from '@/features/crm/components/Modal';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

export function BlackoutForm({
  agreementId,
  onClose,
  onSaved,
  testId = 'agreement-blackout-form',
}: {
  agreementId: string;
  onClose: () => void;
  onSaved: () => void;
  testId?: string;
}) {
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch('/api/btp/agreement-blackouts', {
        method: 'POST',
        body: JSON.stringify({
          agreement_id: agreementId,
          starts_on: startsOn,
          // A single night is the same date twice, so an empty end means the
          // start — which is what somebody entering one date intends.
          ends_on: endsOn || startsOn,
          notes,
        }),
      });
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : 'The blackout could not be saved.',
      );
      setSaving(false);
    }
  }

  return (
    <Modal
      actions={
        <>
          <button
            className="btn btn-outline"
            data-testid={`${testId}-cancel`}
            disabled={saving}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            data-testid={`${testId}-save`}
            disabled={saving || !startsOn}
            onClick={() => void save()}
            type="button"
          >
            {saving ? 'Saving…' : 'Add blackout'}
          </button>
        </>
      }
      onClose={onClose}
      testId={testId}
      title="Add blackout dates"
    >
      <form
        data-testid={`${testId}-form`}
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <p className="page-intro" style={{ marginTop: 0 }}>
          Both dates are included in the blackout. For a single night, leave the
          end date empty.
        </p>

        {error && (
          <div
            className="banner banner-error"
            data-testid={`${testId}-error`}
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="form-grid">
          <div>
            <label className="field-label" htmlFor={`${testId}-starts`}>
              First night
            </label>
            <input
              className="input"
              data-testid={`${testId}-starts`}
              id={`${testId}-starts`}
              onChange={(e) => setStartsOn(e.target.value)}
              type="date"
              value={startsOn}
            />
          </div>
          <div>
            <label className="field-label" htmlFor={`${testId}-ends`}>
              Last night
            </label>
            <input
              className="input"
              data-testid={`${testId}-ends`}
              id={`${testId}-ends`}
              onChange={(e) => setEndsOn(e.target.value)}
              type="date"
              value={endsOn}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="field-label" htmlFor={`${testId}-notes`}>
              Note
            </label>
            <input
              className="input"
              data-testid={`${testId}-notes`}
              id={`${testId}-notes`}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why these dates are blacked out"
              value={notes}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
